import React, { useEffect, useState, useRef } from "react";

import "./styles/createProductListLayer.css";
import "@mdi/font/css/materialdesignicons.min.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import catetgoryProvider from "../apiProvider/categoryapi";
import subCategory from "../apiProvider/subcategoryapi";
import childCategory from "../apiProvider/childcategoryapi";
import BrandApi from "../apiProvider/brand";
import Attribute from "../apiProvider/attribute";
import { Button, ActionIcon, TextInput, Modal, Badge, Group, Stack, Text, ScrollArea, Loader, Tooltip, Checkbox, Radio, FileInput } from "@mantine/core";
import { IconPlus, IconTrash, IconColumns, IconSearch, IconX, IconCheck } from "@tabler/icons-react";
import vendorApi from "../apiProvider/vendor";
import productApi from "../apiProvider/product";
import variantApis from "../apiProvider/variant";
import currencyApi from "../apiProvider/currencyapi";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleFormProduct } from "../redux/slices/product";
import { IMAGE_BASE_URL } from "../network/apiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";


// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

// These always appear — cannot be removed by admin
const COMMON_COLUMNS = [

  // { key: "sku", label: "SKU", type: "text", placeholder: "SKU-001" },
  { key: "stock", label: "Stock", type: "number", placeholder: "0" },
  // { key: "weight", label: "Weight", type: "number", placeholder: "0.00" },
  // { key: "customermrp", label: "Cust. MRP", type: "number", placeholder: "0.00" },
];


const ORING_CATEGORY_NAME = "o-rings";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const buildEmptyRow = (extraColumns, currencies) => {
  const row = {};
  extraColumns.forEach((col) => { row[col.key] = ""; });
  COMMON_COLUMNS.forEach((col) => { row[col.key] = ""; });
  row.pricing = (currencies || []).map(c => ({
    currencyId: c._id,
    currencyCode: c.code,
    price: ""
  }));
  return row;
};

const CreateProductListLayer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selector = useSelector((state) => state.product);
  const uploadInputRef = useRef(null);


  // ── Section toggles
  const [isExpanded, setIsExpanded] = useState(true);
  const [isTechnicalSpecsOpen, setIsTechnicalSpecsOpen] = useState(false);
  const [isVariantOpen, setIsVariantOpen] = useState(false);
  const [isMetaTagsOpen, setIsMetaTagsOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [variantSearch, setVariantSearch] = useState("");
  const [isCreatingVariant, setIsCreatingVariant] = useState(false);
  const [isFilterCheckBox, setIsFilterCheckBox] = useState(false);
  const [isEditingExistVariant, setIsEditingExistVariant] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState(null);

  // ── State
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [taxes, setTaxes] = useState([]);

  // ── Dropdown data
  const [categoryData, setCategories] = useState([]);
  const [subCategoryData, setSubCategory] = useState([]);
  const [childCategoryData, setchldCategory] = useState([]);
  const [brandData, setBrand] = useState([]);
  const [attributeData, setAttribute] = useState([]);
  const [vendorData, setVendor] = useState([]);
  const [availableExtraColumns, setAvailableExtraColumns] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState(null);

  // ── O-ring sizes
  const [oringRows, setOringRows] = useState([]);

  // ── Variant table
  // selectedExtraColumns = columns admin chose from AVAILABLE_EXTRA_COLUMNS
  const [selectedExtraColumns, setSelectedExtraColumns] = useState([]);
  const [variantRows, setVariantRows] = useState([]);

  // Separate O-ring variants
  const [asSelectedExtraColumns, setAsSelectedExtraColumns] = useState([]);
  const [jisSelectedExtraColumns, setJisSelectedExtraColumns] = useState([]);

  // Column picker target
  const [pickerTarget, setPickerTarget] = useState("common"); // "common", "as", "jis"

  // Full column list = selected extra + common (always appended at end)
  const fullColumnList = [...selectedExtraColumns, ...COMMON_COLUMNS];

  // ── Form data
  const [formData, setFormData] = useState({
    category: "",
    subCategory: "",
    childCategory: "",
    brand: "",
    productName: "",
    productImage: [],
    additionalImages: [],
    shortDescription: "",
    slug: "",
    lowStockAlert: false,
    tagsLabel: "Select",
    refundable: "Yes",
    productStatus: "active",
    description: "",
    hsn: "",
    customerDiscunt: "",
    customerTax: "",
    wholeSalerDiscunt: "",
    wholeSalerTax: "",
    wholesalerAttribute: [],
    customerAttribute: [],
    metaTitle: "",
    metaKey: "",
    metaDescription: "",
    selectDeliveryOption: "",
    vendorNameSelect: "",
    isApplicableToCustomer: false,
    isApplicableToWholesaler: false,
    lowStockQuantity: 0,
    quantityPerPack: 0,
    packingType: "",
    tax: "",
  });

  // Technical Specs Input Method State
  const [asInputMethod, setAsInputMethod] = useState("manual"); // "manual" or "file"
  const [jisInputMethod, setJisInputMethod] = useState("manual"); // "manual" or "file"
  const [asFile, setAsFile] = useState(null);
  const [jisFile, setJisFile] = useState(null);

  // Derived
  const isOringCategory = (() => {
    const subcat = subCategoryData.find((s) => s._id === formData.subCategory);
    return !!subcat?.origins;
  })();

  const isOthersCategory = (() => {
    const subcat = subCategoryData.find((s) => s._id === formData.subCategory);
    return !!subcat?.others;
  })();

  const isVariantsCategory = (() => {
    const subcat = subCategoryData.find((s) => s._id === formData.subCategory);
    return !!subcat?.variants;
  })();

  // Columns not yet added (available to pick) for CURRENT TARGET
  const unselectedColumns = availableExtraColumns.filter((col) => {
    const currentList = pickerTarget === "as" ? asSelectedExtraColumns : pickerTarget === "jis" ? jisSelectedExtraColumns : selectedExtraColumns;
    return !currentList.find((s) => s.key === col.key);
  });

  // ─── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const [cats, currs] = await Promise.all([
          categoryDtls(), fetchCurrency(), brandsDtls(), attributeDtls(), vendorDtls(), fetchTaxes(), fetchVariants()
        ]);
        if (selector.isEdit) {
          setIsEditing(true);
          await setUpdatedData(cats, currs);
        }
      } catch (e) { console.error(e); }
    };
    init();
  }, []);

  const fetchVariants = async () => {
    try {
      const res = await variantApis.variantList({ page: 0, limit: 1000 });
      if (res.status) {
        const dynamicCols = res.response.data.map(v => ({
          _id: v._id,
          key: v.name.toLowerCase().replace(/\s+/g, "_"),
          label: v.name,
          isFilter: v.isFilter,
        }));
        setAvailableExtraColumns(dynamicCols);
        return dynamicCols;
      }
    } catch (e) { console.error("Error fetching variants:", e); }
    return [];
  };

  const fetchCurrency = async () => {
    try {
      const res = await currencyApi.getCurrencies({ page: 0, limit: 100, status: true });
      if (res.status) {
        const data = res.response?.data || [];
        setCurrencies(data);
        return data;
      }
    } catch (e) { console.error("Error fetching currencies:", e); }
    return [];
  };

  const getCurrencyProps = (currencyId) => {
    const c = currencies.find(curr => curr._id === currencyId)
    return {
      code: c?.code || "",
      symbol: c?.symbol || ""
    };
  };

  const fetchTaxes = async () => {
    const res = await productApi.getTax();
    if (res.status) setTaxes(res.response.data);
  };

  // ─── Cascading dropdowns ───────────────────────────────────────────────────
  const categoryDtls = async () => {
    const params = selector.isEdit ? {} : { status: true };
    const res = await catetgoryProvider.getCategory(params);
    if (res) {
      const cats = res?.data?.data?.items || [];
      setCategories(cats);
      return cats;
    }
    return [];
  };

  useEffect(() => {
    subCategoryDtls();
  }, [formData.category]);
  const subCategoryDtls = async () => {
    if (!formData.category) { setSubCategory([]); return; }
    setLoading(true);
    try {
      const params = { categoryId: formData.category, limit: 100, page: 0 };
      if (!selector.isEdit) params.status = true;
      const res = await subCategory.getSubCategorys(params);
      if (res?.status) setSubCategory(res.response?.data?.data?.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (formData.subCategory) {
      childCategoryDtls(formData.subCategory);
    }
  }, [formData.subCategory]);
  const childCategoryDtls = async (id) => {
    try {
      const res = await childCategory.getChildCategoryBysubCategoryId(id);
      if (res?.status) setchldCategory(res.response?.data || []);
    } catch { setchldCategory([]); }
  };

  const brandsDtls = async () => { const r = await BrandApi.brandList({ type: "all", page: 0, limit: 100 }); if (r?.status) setBrand(r.response.data); };
  const attributeDtls = async () => { const r = await Attribute.attributeList({ type: "all" }); if (r?.status) setAttribute(r.response.data); };
  const vendorDtls = async () => { const r = await vendorApi.vendorList({ type: "all" }); if (r?.status) setVendor(r.response.data); };

  // ─── Column picker ─────────────────────────────────────────────────────────

  const handleAddExtraColumn = (col) => {
    if (pickerTarget === "as") {
      setAsSelectedExtraColumns((prev) => [...prev, col]);
    } else if (pickerTarget === "jis") {
      setJisSelectedExtraColumns((prev) => [...prev, col]);
    } else {
      setSelectedExtraColumns((prev) => [...prev, col]);
      setVariantRows((prev) => prev.map((row) => ({ ...row, [col.key]: "" })));
    }
    setShowColumnPicker(false);
    setVariantSearch("");
  };

  const handleCreateNewVariant = async () => {
    if (!variantSearch.trim()) return;
    try {
      setIsCreatingVariant(true);
      const res = await variantApis.variantCreate({ name: variantSearch.trim(), isFilter: isFilterCheckBox });
      if (res.status) {
        const dynamicCols = await fetchVariants();
        const newCol = dynamicCols.find(c => c.label.toLowerCase() === variantSearch.trim().toLowerCase());
        if (newCol) {
          handleAddExtraColumn(newCol);
          setShowColumnPicker(false);
          setVariantSearch("");
          setIsFilterCheckBox(false);
        }
      }
    } catch (e) {
      console.error("Error creating variant:", e);
    } finally {
      setIsCreatingVariant(false);
    }
  };

  const handleUpdateVariant = async () => {
    if (!variantSearch.trim() || !editingVariantId) return;
    try {
      setIsCreatingVariant(true);
      const res = await variantApis.variantUpdate(editingVariantId, { name: variantSearch.trim(), isFilter: isFilterCheckBox });
      if (res.status) {
        await fetchVariants();
        setIsEditingExistVariant(false);
        setEditingVariantId(null);
        setVariantSearch("");
        setIsFilterCheckBox(false);
      }
    } catch (e) {
      console.error("Error updating variant:", e);
    } finally {
      setIsCreatingVariant(false);
    }
  };

  const startEditVariant = (col) => {
    setIsEditingExistVariant(true);
    setEditingVariantId(col._id);
    setVariantSearch(col.label);
    setIsFilterCheckBox(!!col.isFilter);
  };

  const cancelEditVariant = () => {
    setIsEditingExistVariant(false);
    setEditingVariantId(null);
    setVariantSearch("");
    setIsFilterCheckBox(false);
  };

  const handleRemoveExtraColumn = (colKey, target = "common") => {
    if (target === "as") {
      setAsSelectedExtraColumns((prev) => prev.filter((c) => c.key !== colKey));
    } else if (target === "jis") {
      setJisSelectedExtraColumns((prev) => prev.filter((c) => c.key !== colKey));
    } else {
      setSelectedExtraColumns((prev) => prev.filter((c) => c.key !== colKey));
      // Remove key from all rows
      setVariantRows((prev) =>
        prev.map((row) => { const r = { ...row }; delete r[colKey]; return r; })
      );
    }
  };

  // ─── Variant row handlers ──────────────────────────────────────────────────
  const handleAddVariantRow = () => {
    setVariantRows((prev) => [...prev, buildEmptyRow(selectedExtraColumns, currencies)]);
    if (!isVariantOpen) setIsVariantOpen(true);
  };

  const handleVariantRowChange = (index, field, value) => {
    setVariantRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      if (field.startsWith("price_")) {
        const cId = field.replace("price_", "");
        row.pricing = (row.pricing || []).map(p =>
          p.currencyId === cId ? { ...p, price: value } : p
        );
      } else {
        row[field] = value;
      }
      updated[index] = row;
      return updated;
    });
    // Clear error
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`v_${field}_${index}`];
      // If price or mrp changed, clear the comparison error too
      if (field === "price" || field === "customermrp") {
        delete newErrors[`v_customermrp_${index}`];
      }
      return newErrors;
    });
  };

  const handleRemoveVariantRow = (index) => {
    setVariantRows((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Excel Handlers ────────────────────────────────────────────────────────

  const handleDownloadTemplate = () => {
    const headers = [
      "_id",
      ...selectedExtraColumns.map((c) => c.label),

      ...COMMON_COLUMNS.map((c) => c.label),
      ...currencies.map((c) => `${c.code} Price`),
    ];

    const rows =
      variantRows.length > 0
        ? variantRows.map((row) => [
          row._id || "",
          ...selectedExtraColumns.map((c) => row[c.key] || ""),
          ...COMMON_COLUMNS.map((c) => row[c.key] || ""),
          ...currencies.map(
            (c) => row.pricing?.find((p) => p.currencyId === c._id)?.price || ""
          ),
        ])
        : [new Array(headers.length).fill("")]; // 1 empty row

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet["!cols"] = [{ hidden: true }]; // Hide _id column
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Variants");
    XLSX.writeFile(workbook, "variant_template.xlsx");
    toast.success("Template downloaded successfully");
  };

  const handleUploadExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls") && !fileName.endsWith(".csv")) {
      toast.error("Please upload an Excel (.xlsx/.xls) or CSV file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 1) {
          toast.error("The file is empty");
          return;
        }

        const headers = jsonData[0];
        const dataRows = jsonData.slice(1).filter((r) => r && r.some((cell) => cell !== null && cell !== ""));

        if (dataRows.length === 0) {
          toast.error("No valid data rows found in the file");
          return;
        }

        // Header classify
        const headerMap = headers.map((header) => {
          if (!header) return { type: "skip" };
          const headerStr = header.toString().trim();
          console.log("headerStr", headerStr);

          // ID match
          if (headerStr === "_id") return { type: "id", key: "_id" };

          // Currency match
          const curr = currencies.find((c) => `${c.code} Price` === headerStr);
          if (curr) return { type: "currency", currencyId: curr._id, currencyCode: curr.code };

          // Common match
          const common = COMMON_COLUMNS.find((c) => c.label === headerStr);
          if (common) return { type: "common", key: common.key };

          // Extra match (already selected)
          const extra = selectedExtraColumns.find((c) => c.label === headerStr);
          if (extra) return { type: "extra", key: extra.key };

          // New Extra (available but not selected)
          const available = availableExtraColumns.find((c) => c.label === headerStr);
          if (available) return { type: "newExtra", col: available };

          return { type: "skip" };
        });

        const skippedHeaders = headers.filter((h, idx) => h && headerMap[idx].type === "skip");
        const newCols = headerMap.filter((m) => m.type === "newExtra").map((m) => m.col);

        // Update selected columns if new ones found
        if (newCols.length > 0) {
          setSelectedExtraColumns((prev) => [...prev, ...newCols]);
          setVariantRows((prev) =>
            prev.map((r) => ({
              ...r,
              ...newCols.reduce((acc, c) => ({ ...acc, [c.key]: "" }), {}),
            }))
          );
          toast.info(`Added new columns: ${newCols.map((c) => c.label).join(", ")}`);
        }

        if (skippedHeaders.length > 0) {
          toast.warning(`Skipped unknown columns: ${skippedHeaders.join(", ")}`);
        }

        // Build parsed rows
        const parsedRows = dataRows.map((rowArr) => {
          const rowObj = { pricing: [] };
          headerMap.forEach((mapping, idx) => {
            const value = rowArr[idx] !== undefined && rowArr[idx] !== null ? rowArr[idx].toString().trim() : "";
            if (mapping.type === "id") {
              rowObj[mapping.key] = value;
            } else if (mapping.type === "common" || mapping.type === "extra" || mapping.type === "newExtra") {
              rowObj[mapping.key || mapping.col.key] = value;
            } else if (mapping.type === "currency") {
              rowObj.pricing.push({
                currencyId: mapping.currencyId,
                currencyCode: mapping.currencyCode,
                price: value,
              });
            }
          });

          // Ensure all currencies are present in pricing
          currencies.forEach((c) => {
            if (!rowObj.pricing.find((p) => p.currencyId === c._id)) {
              rowObj.pricing.push({ currencyId: c._id, currencyCode: c.code, price: "" });
            }
          });

          return rowObj;
        });
        console.log("setVariantRows", variantRows);
        console.log("parsedRows", parsedRows);

        setVariantRows(parsedRows);
        toast.success(`${parsedRows.length} rows imported successfully`);
      } catch (error) {
        console.error("Excel parse error:", error);
        toast.error("Failed to parse Excel file");
      }
      // Reset input
      if (e.target) e.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };


  // ─── O-ring size handlers ──────────────────────────────────────────────────
  const blankOringRow = (standard) => {
    const row = {
      standard,   // "AS568A" or "JIS B 2401"
      sizeCode: "",
      metric_id_mm: "",
      metric_id_tolerance_mm: "",
      metric_cs_mm: "",
      metric_cs_tolerance_mm: "",
      // baseSku: "",
      materials: [],
    };
    // spread extra columns based on standard
    const extra = standard === "AS568A" ? asSelectedExtraColumns : jisSelectedExtraColumns;
    extra.forEach(col => {
      row[col.key] = "";
    });
    return row;
  };

  const handleAddAS568ASize = () =>
    setOringRows(prev => [...prev, blankOringRow("AS568A")]);

  const handleAddJISSize = () =>
    setOringRows(prev => [...prev, blankOringRow("JIS B 2401")]);

  const handleSizeChange = (standard, index, field, value) => {
    const numericFields = ["metric_id_mm", "metric_id_tolerance_mm", "metric_cs_mm", "metric_cs_tolerance_mm"];
    const numericValue = (numericFields.includes(field) && value !== "") ? Math.max(0, parseFloat(value) || 0).toString() : value;

    setOringRows(prev => {
      // find the Nth row of this standard
      let count = -1;
      return prev.map(row => {
        if (row.standard === standard) count++;
        if (row.standard === standard && count === index) {
          return { ...row, [field]: value };
        }
        return row;
      });
    });
    // Clear error
    setErrors((prev) => {
      const standardKey = standard === "AS568A" ? "as_568a_standard" : "jis_b_2401_standard";
      const newErrors = { ...prev };
      delete newErrors[`${standardKey}_${field}_${index}`];
      return newErrors;
    });
  };

  const handleRemoveSize = (standard, index) => {
    setOringRows(prev => {
      let count = -1;
      return prev.filter(row => {
        if (row.standard === standard) {
          count++;
          if (count === index) return false;
        }
        return true;
      });
    });
  };

  const as568Rows = oringRows.filter(r => r.standard === "AS568A");
  const jisRows = oringRows.filter(r => r.standard === "JIS B 2401");

  // ─── Slug auto-generate ────────────────────────────────────────────────────
  useEffect(() => {
    const slug = formData.productName
      ?.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-+|-+$/g, "");
    setFormData((prev) => ({ ...prev, slug }));
  }, [formData.productName]);

  // ─── Edit mode ─────────────────────────────────────────────────────────────
  const urlToFile = async (url, name, mime) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new File([blob], name, { type: mime });
  };

  const setUpdatedData = async (catData, currenciesList) => {
    const activeCurrencies = currenciesList || currencies;
    setLoading(true);
    const response = await productApi.productDetails(selector.id);
    if (response.status) {
      const resp = response.response.data;
      const existingImages = await Promise.all((resp.productImage || []).map((img) => urlToFile(`${IMAGE_BASE_URL}/${img.docPath}/${img.docName}`, img.originalName || img.docName, "image/png")));
      const additionalImgs = await Promise.all((resp.additionalImage || []).map((img) => urlToFile(`${IMAGE_BASE_URL}/${img.docPath}/${img.docName}`, img.originalName || img.docName, "image/png")));

      setFormData({
        category: resp.categoryId || "", subCategory: resp.subCategory || "",
        childCategory: resp.childCategory || "", brand: resp.brand || "",
        productName: resp.productName || "", hsn: resp.hsn || "",
        productImage: existingImages, additionalImages: additionalImgs,
        shortDescription: resp.shortDescription || "", slug: resp.slug || "",
        lowStockAlert: resp.lowStockAlert || false,
        tagsLabel: resp.tagAndLabel || "Select",
        refundable: resp.refundable ? "Yes" : "No",
        productStatus: resp.productStatus ? "active" : "inactive",
        description: resp.description || "",
        lowStockQuantity: resp.lowStockQuantity || "",
        customerDiscunt: resp.customerDiscount?.toString() || "",
        customerTax: resp.customerTax?.toString() || "",
        wholeSalerDiscunt: resp.wholesalerDiscount?.toString() || "",
        wholeSalerTax: resp.wholesalerTax?.toString() || "",
        wholesalerAttribute: [],
        customerAttribute: [],
        metaTitle: resp.metaTitle || "", metaKey: resp.metaKeyword || "",
        metaDescription: resp.metaDesc || "",
        selectDeliveryOption: resp.delivery || "",
        vendorNameSelect: resp.vendorId || "",
        isApplicableToCustomer: resp.applicableForCustomer || false,
        isApplicableToWholesaler: resp.applicableForWholesale || false,
        quantityPerPack: resp.quantityPerPack || 0,
        packingType: resp.packingType || "",
        tax: resp.tax || "",
      });


      // Detect which extra columns were saved
      const isOring = await (async () => {
        // We need subcategories for this category to check the 'origins' flag
        const params = { categoryId: resp.categoryId, limit: 100, page: 0 };
        if (!selector.isEdit) params.status = true;
        const subRes = await subCategory.getSubCategorys(params);
        if (subRes?.status) {
          const subs = subRes.response?.data?.data?.items || [];
          setSubCategory(subs); // Update state for the dropdown
          const subcat = subs.find((s) => s._id === resp.subCategory);
          return !!subcat?.origins;
        }
        return false;
      })();

      let savedRows = [];
      if (isOring) {
        savedRows = resp.customerAttribute ? resp.customerAttribute : [];
      } else {
        savedRows = resp.customerAttribute ? resp.customerAttribute : [];
      }

      if (savedRows.length > 0) {
        const firstRow = savedRows[0];
        // We wait for variants to be loaded before detection or use the freshly fetched ones
        let currentAvailable = availableExtraColumns;
        if (!currentAvailable.length) {
          currentAvailable = await fetchVariants();
        }
        const detected = currentAvailable.filter((col) => col.key in firstRow);

        if (isOring) {
          // Detect separately for O-rings
          const asFirst = savedRows.find(r => r.standard === "AS568A");
          const jisFirst = savedRows.find(r => r.standard === "JIS B 2401");

          const getExtraCols = (firstRow) => {
            if (!firstRow) return [];
            const rowKeys = Object.keys(firstRow).map(k => k.toLowerCase());
            return currentAvailable.filter(col =>
              rowKeys.includes(col.key.toLowerCase()) &&
              !["_id", "standard", "sizeCode", "metric_id_mm", "metric_id_tolerance_mm", "metric_cs_mm", "metric_cs_tolerance_mm", "sku", "stock", "weight", "customermrp", "price", "materials"].includes(col.key.toLowerCase())
            );
          };

          if (asFirst) setAsSelectedExtraColumns(getExtraCols(asFirst));
          if (jisFirst) setJisSelectedExtraColumns(getExtraCols(jisFirst));

          const mapRowPrices = (row) => {
            // Normalize keys to lowercase to match variant keys (thickness vs Thickness)
            const normalizedRow = {};
            Object.keys(row).forEach(k => {
              if (["_id", "standard", "sizeCode", "metric_id_mm", "metric_id_tolerance_mm", "metric_cs_mm", "metric_cs_tolerance_mm", "baseSku", "materials"].includes(k)) {
                normalizedRow[k] = row[k];
              } else {
                normalizedRow[k.toLowerCase()] = row[k];
              }
            });

            const newRow = { ...normalizedRow };
            const currentPricing = Array.isArray(row.pricing) ? row.pricing : [];
            newRow.pricing = (activeCurrencies || []).map(c => {
              let priceValue = "";
              const existing = currentPricing.find(p => p.currencyId === c._id);
              if (existing) priceValue = existing.price;

              return {
                currencyId: c._id,
                currencyCode: c.code,
                price: priceValue
              };
            });
            return newRow;
          };

          setOringRows(savedRows.map(mapRowPrices));
          setIsTechnicalSpecsOpen(true);
        } else {
          setSelectedExtraColumns(detected);
          const mapRowPrices = (row) => {
            const newRow = { ...row };


            const currentPricing = Array.isArray(row.pricing) ? row.pricing : [];
            newRow.pricing = (activeCurrencies || []).map(c => {
              let priceValue = "";
              const existing = currentPricing.find(p => p.currencyId === c._id);
              if (existing) priceValue = existing.price;

              return {
                currencyId: c._id,
                currencyCode: c.code,
                price: priceValue
              };
            });
            return newRow;
          };
          const savedRowsDirect = Array.isArray(resp.customerAttribute) ? resp.customerAttribute : (resp.customerAttribute?.rowData || []);
          setVariantRows(savedRowsDirect.map(row => mapRowPrices({ ...row })));
          setIsVariantOpen(true);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isOringCategory) return;
    setOringRows(prev => prev.map(row => {
      const extra = row.standard === "AS568A" ? asSelectedExtraColumns : jisSelectedExtraColumns;
      const newRow = { ...row };
      extra.forEach(col => {
        if (!(col.key in newRow)) newRow[col.key] = "";
      });
      const staticKeys = [
        "_id",
        "standard", "sizeCode", "metric_id_mm", "metric_id_tolerance_mm",
        "metric_cs_mm", "metric_cs_tolerance_mm", "baseSku", "materials"
      ];

      Object.keys(newRow).forEach(key => {
        if (!staticKeys.includes(key) && !extra.find(c => c.key === key)) {
          delete newRow[key];
        }
      });
      return newRow;
    }));
  }, [asSelectedExtraColumns, jisSelectedExtraColumns, isOringCategory]);

  // ─── Form handlers ─────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Enforce non-negativity for known numeric fields
    const decimalFields = ["customerDiscunt", "customerTax", "wholeSalerDiscunt", "wholeSalerTax"];
    const intFields = ["quantityPerPack", "lowStockQuantity", "hsn"];
    let finalValue = value;

    if (decimalFields.includes(name) && value !== "") {
      finalValue = Math.max(0, parseFloat(value) || 0).toString();
    } else if (intFields.includes(name) && value !== "") {
      finalValue = Math.max(0, parseInt(value) || 0).toString();
    }

    setFormData((p) => ({ ...p, [name]: finalValue }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };
  const handleChangeCheckBox = (e) => { const { name, value, type, checked } = e.target; setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value })); };
  const handleEditorChange = (_, editor) => setFormData((p) => ({ ...p, description: editor.getData() }));
  const handleDeleteImage = (i) => setFormData((p) => ({ ...p, productImage: p.productImage.filter((_, idx) => idx !== i) }));
  const handleDeleteAdditionalImage = (i) => setFormData((p) => ({ ...p, additionalImages: p.additionalImages.filter((_, idx) => idx !== i) }));

  const handleFileChange = (e, key) => {
    const files = Array.from(e.target.files); const allowed = ["image/jpeg", "image/png"];
    const valid = files.filter((f) => { if (!allowed.includes(f.type)) { alert(`${f.name} is not valid JPG/PNG`); return false; } return true; });
    if (!valid.length) return;
    setFormData((p) => ({ ...p, [key]: key === "productImage" ? [valid[0]] : [...(p[key] || []), ...valid] }));
  };

  // ─── Validation ────────────────────────────────────────────────────────────
  const Validate = () => {
    const newErrors = {};
    const req = (val, key, msg) => { if (!val || (typeof val === "string" && !val.trim()) || (Array.isArray(val) && !val.length)) newErrors[key] = msg; };
    req(formData.category, "category", "Category is Required");
    req(formData.subCategory, "subCategory", "Sub Category is Required");
    // req(formData.childCategory, "childCategory", "Child Category is Required");
    req(formData.brand, "brand", "Brand is Required");
    req(formData.productName, "productName", "Product Name is Required");
    req(formData.slug, "slug", "Slug is Required");
    req(formData.packingType, "packingType", "Packing Type is Required");
    if (!formData.productImage?.length) newErrors.productImage = "At least one product image is required";
    if (!formData.hsn) newErrors.hsn = "HSN code is required";
    else if (!/^\d+$/.test(formData.hsn)) newErrors.hsn = "HSN code must be numeric";
    else if (![4, 6, 8].includes(formData.hsn.length)) newErrors.hsn = "HSN code must be 4, 6, or 8 digits";
    if (!formData.quantityPerPack) newErrors.quantityPerPack = "Quantity Per Pack is Required";
    else if (Number(formData.quantityPerPack) <= 0) newErrors.quantityPerPack = "Quantity must be > 0";

    if (!isOringCategory && variantRows.length > 0) {
      variantRows.forEach((row, i) => {
        fullColumnList.forEach(({ key, label, type }) => {
          if (key === "currency" || key === "price") return;
          const val = row[key];

          // Only validate required for common columns
          const isCommon = COMMON_COLUMNS.some(c => c.key === key);
          if (isCommon && (!val || val.toString().trim() === "")) {
            newErrors[`v_${key}_${i}`] = `${label} is required`;
          } else if (val && type === "number") {
            if (isNaN(Number(val))) newErrors[`v_${key}_${i}`] = `${label} must be a number`;
            else if (Number(val) < 0) newErrors[`v_${key}_${i}`] = `${label} cannot be negative`;
          }
        });
        (row.pricing || []).forEach((p) => {
          const val = p.price;
          const currency = currencies.find(c => c._id === p.currencyId);
          const cCode = currency?.code || "Currency";
          if (!val || val.toString().trim() === "") newErrors[`v_price_${p.currencyId}_${i}`] = `${cCode} Price is required`;
          else if (isNaN(Number(val))) newErrors[`v_price_${p.currencyId}_${i}`] = `${cCode} Price must be a number`;
          else if (Number(val) < 0) newErrors[`v_price_${p.currencyId}_${i}`] = `${cCode} Price cannot be negative`;
        });
      });
    }

    if (isOringCategory) {
      // AS 568A Standard Validation
      if (asInputMethod === "file") {
        if (!asFile) newErrors.asFile = "Please upload a file for AS 568A Standard";
      } else {
        const asRows = oringRows.filter(r => r.standard === "AS568A");
        asRows.forEach((row, i) => {
          ["sizeCode", "metric_id_mm", "metric_id_tolerance_mm", "metric_cs_mm", "metric_cs_tolerance_mm"].forEach(field => {
            const val = row[field];
            if (!val || val.toString().trim() === "") {
              newErrors[`as_568a_standard_${field}_${i}`] = "Required";
            } else if (field !== "sizeCode" && field !== "baseSku") {
              if (isNaN(Number(val))) newErrors[`as_568a_standard_${field}_${i}`] = "Number required";
              else if (Number(val) < 0) newErrors[`as_568a_standard_${field}_${i}`] = "Cannot be negative";
            }
          });
          // Extra columns are no longer required
        });
      }

      // JIS B 2401 Standard Validation
      if (jisInputMethod === "file") {
        if (!jisFile) newErrors.jisFile = "Please upload a file for JIS B 2401 Standard";
      } else {
        const jisRows = oringRows.filter(r => r.standard === "JIS B 2401");
        jisRows.forEach((row, i) => {
          ["sizeCode", "metric_id_mm", "metric_id_tolerance_mm", "metric_cs_mm", "metric_cs_tolerance_mm"].forEach(field => {
            const val = row[field];
            if (!val || val.toString().trim() === "") {
              newErrors[`jis_b_2401_standard_${field}_${i}`] = "Required";
            } else if (field !== "sizeCode" && field !== "baseSku") {
              if (isNaN(Number(val))) newErrors[`jis_b_2401_standard_${field}_${i}`] = "Number required";
              else if (Number(val) < 0) newErrors[`jis_b_2401_standard_${field}_${i}`] = "Cannot be negative";
            }
          });
          // Extra columns are no longer required
        });
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const buildFormData = () => {
    const fd = new FormData();
    fd.append("categoryId", formData.category);
    fd.append("subCategory", formData.subCategory);
    fd.append("productName", formData.productName);
    fd.append("brand", formData.brand)
    fd.append("shortDescription",
      formData.shortDescription);
    fd.append("slug", formData.slug);
    fd.append("hsn", formData.hsn);
    fd.append("lowStockAlert", String(formData.lowStockAlert === true));
    fd.append("tagAndLabel", formData.tagsLabel);
    fd.append("refundable", String(formData.refundable === "Yes" || formData.refundable === true));
    fd.append("productStatus", String(formData.productStatus === "active"));
    fd.append("description", formData.description);
    fd.append("applicableForCustomer", String(formData.isApplicableToCustomer === true));
    fd.append("customerDiscount", String(Number(formData.customerDiscunt)));
    fd.append("customerTax", String(Number(formData.customerTax)));
    fd.append("applicableForWholesale", String(formData.isApplicableToWholesaler === true));
    fd.append("wholesalerDiscount", String(Number(formData.wholeSalerDiscunt)));
    fd.append("wholesalerTax", String(Number(formData.wholeSalerTax)));
    fd.append("metaTitle", formData.metaTitle);
    fd.append("metaKeyword", formData.metaKey);
    fd.append("metaDesc", formData.metaDescription);
    fd.append("delivery", formData.selectDeliveryOption);
    fd.append("lowStockQuantity", String(Number(formData.lowStockQuantity) || 0));
    fd.append("quantityPerPack", formData.quantityPerPack);
    fd.append("packingType", formData.packingType);
    fd.append("tax", formData.tax);
    (formData.productImage || []).forEach((img) => { if (img instanceof File) fd.append("productImage", img); });
    (formData.additionalImages || []).forEach((img) => { fd.append("additionalImages", img); });

    // Technical Specs (O-ring) - Handles both file and manual attributes
    if (isOringCategory) {
      if (asInputMethod === "file" && asFile) fd.append("asAttributeFile", asFile);
      if (jisInputMethod === "file" && jisFile) fd.append("jisAttributeFile", jisFile);

      // Always send all oringRows present in the table to the backend
      if (oringRows.length > 0) {
        const cleanRows = oringRows.map(({ pricing, ...rest }) => rest);
        fd.append("customerAttribute", JSON.stringify(cleanRows));
      }
    }
    // Variant logic (customerAttribute) for other categories
    else if ((isVariantsCategory || isOthersCategory) && variantRows.length > 0) {
      // Variants/Others - standard pricing array at row level, preserve materials if any
      const stripped = variantRows.map(({ pricing, ...rest }) => ({
        ...rest,
        pricing: (pricing || []).map(({ currencyCode, ...restPrices }) => restPrices)
      }));
      console.log("Sending Variant/Other customerAttribute:", JSON.stringify(stripped, null, 2));
      fd.append("customerAttribute", JSON.stringify(stripped));
    }
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!Validate()) return;
    const fd = buildFormData();
    console.log("=== FormData Contents ===");
    for (let [key, value] of fd.entries()) {
      if (value instanceof File) {
        console.log(key, ":", value.name, `(${value.type})`);
      } else {
        console.log(key, ":", value);
      }
    }
    console.log("========================");

    if (isEditing) {
      fd.append("id", selector.id);
      const r = await productApi.updateproduct(fd, selector.id);
      if (r.status) {
        setTimeout(handleClose, 2000);
      }
    }
    else {
      const r = await productApi.productcreate(fd);
      if (r.status) {
        setTimeout(handleClose, 2000);
      }
    }
  };

  const handleClose = () => {
    dispatch(handleFormProduct({ view: false, isEdit: false, id: "" }));
    setOringRows([]);
    setVariantRows([]);
    setSelectedExtraColumns([]);
    setAsSelectedExtraColumns([]);
    setJisSelectedExtraColumns([]);
    setIsTechnicalSpecsOpen(false);
    setIsVariantOpen(false);
    setIsFilterCheckBox(false);
    setIsEditingExistVariant(false);
    setEditingVariantId(null);
    setVariantSearch("");
    setAsInputMethod("manual");
    setJisInputMethod("manual");
    setAsFile(null);
    setJisFile(null);
    setTimeout(() => navigate("/product"), 300);
  };

  if (isLoading) return <div className="p-5 text-center">Loading...</div>;

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div id="addproduct-accordion" className="custom-accordion">

                {/* ══════════════════════════════════════════════════
                  01 — GENERAL INFO
              ══════════════════════════════════════════════════ */}
                <div className="card mb-4">
                  <a href="#gen-collapse" className={`text-body ${isExpanded ? "" : "collapsed"}`}
                    data-bs-toggle="collapse" aria-expanded={isExpanded}
                    onClick={(e) => { e.preventDefault(); setIsExpanded(!isExpanded); }}>
                    <div className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-3">
                          <div className="avatar"><div className="avatar-title custom-avatar rounded-circle bg-primary-subtle text-primary d-flex justify-content-center align-items-center"><h5 className="text-primary font-size-17 mb-0">01</h5></div></div>
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h5 className="font-size-16 mb-1">General Info</h5>
                          <p className="text-muted text-truncate mb-0">Fill all information below</p>
                        </div>
                        <i className={`mdi mdi-chevron-${isExpanded ? "up" : "down"} accor-down-icon font-size-24`}></i>
                      </div>
                    </div>
                  </a>
                  <div id="gen-collapse" className={`collapse ${isExpanded ? "show" : ""}`} data-bs-parent="#addproduct-accordion">
                    <div className="p-4 border-top">
                      <div className="row">
                        <div className="col-lg-4 mb-3">
                          <label className="form-label">Category <span className="text-danger">*</span></label>
                          <select name="category" className="form-control" value={formData.category} onChange={(e) => { handleChange(e); categoryDtls(e.target.value); }}>
                            <option value="">Select Category</option>
                            {categoryData?.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                          </select>
                          {errors.category && <div className="text-danger small">{errors.category}</div>}
                        </div>
                        <div className="col-lg-4 mb-3">
                          <label className="form-label">Sub Category <span className="text-danger">*</span></label>
                          <select name="subCategory" className="form-control" value={formData.subCategory} onChange={handleChange}>
                            <option value="">Select Sub Category</option>
                            {subCategoryData?.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                          </select>
                          {errors.subCategory && <div className="text-danger small">{errors.subCategory}</div>}
                        </div>
                        {/* <div className="col-lg-4 mb-3">
                        <label className="form-label">Child Category</label>
                        <select name="childCategory" className="form-control" value={formData.childCategory} onChange={handleChange}>
                          <option value="">Select Child Category</option>
                          {childCategoryData?.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </select>
                        {errors.childCategory && <div className="text-danger small">{errors.childCategory}</div>}
                      </div> */}
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Brand <span className="text-danger">*</span></label>
                          <select name="brand" className="form-select" value={formData.brand} onChange={handleChange}>
                            <option value="">Select Brand</option>
                            {brandData?.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                          </select>
                          {errors.brand && <div className="text-danger small">{errors.brand}</div>}
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Tax</label>
                          <select name="tax" className="form-select" value={formData.tax} onChange={handleChange}>
                            <option value="">Select Tax</option>
                            {taxes?.map((t) => <option key={t._id} value={t._id}>{t.taxName}</option>)}
                          </select>
                          {errors.tax && <div className="text-danger small">{errors.tax}</div>}
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Product Name <span className="text-danger">*</span></label>
                          <input type="text" name="productName" className="form-control" value={formData.productName} onChange={handleChange} />
                          {errors.productName && <div className="text-danger small">{errors.productName}</div>}
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Short Description</label>
                          <textarea name="shortDescription" className="form-control" rows="1" value={formData.shortDescription} onChange={handleChange} />
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Slug <span className="text-danger">*</span></label>
                          <input type="text" name="slug" className="form-control" value={formData.slug} onChange={handleChange} />
                          {errors.slug && <div className="text-danger small">{errors.slug}</div>}
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Packing Type <span className="text-danger">*</span></label>
                          <select name="packingType" className="form-select" value={formData.packingType} onChange={handleChange}>
                            <option value="">Select</option><option>Bag</option><option>Box</option>
                          </select>
                          {errors.packingType && <div className="text-danger small">{errors.packingType}</div>}
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Quantity Per Pack <span className="text-danger">*</span></label>
                          <input type="number" name="quantityPerPack" className="form-control" min={0} value={formData.quantityPerPack} onKeyDown={e => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                          {errors.quantityPerPack && <div className="text-danger small">{errors.quantityPerPack}</div>}
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">HSN Code <span className="text-danger">*</span></label>
                          <input type="text" name="hsn" className="form-control" value={formData.hsn} onChange={handleChange} />
                          {errors.hsn && <div className="text-danger small">{errors.hsn}</div>}
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Tags &amp; Label</label>
                          <select name="tagsLabel" className="form-control" value={formData.tagsLabel} onChange={handleChange}>
                            <option>Select</option><option>Best Seller</option><option>New Arrival</option><option>Top Rated</option>
                          </select>
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Refundable</label>
                          <select name="refundable" className="form-control" value={formData.refundable} onChange={handleChange}>
                            <option value="Yes">Yes</option><option value="No">No</option>
                          </select>
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Product Status</label>
                          <select name="productStatus" className="form-control" value={formData.productStatus} onChange={handleChange}>
                            <option value="active">Active</option><option value="inactive">Inactive</option>
                          </select>
                        </div>
                        <div className="col-lg-3 mb-3 d-flex align-items-center gap-2 pt-28">
                          <input type="checkbox" id="lowStockAlert" name="lowStockAlert" className="form-check-input mt-0" checked={formData.lowStockAlert} onChange={handleChangeCheckBox} />
                          <label htmlFor="lowStockAlert" className="form-label mb-0">Low Stock Alert</label>
                        </div>
                        {formData.lowStockAlert && (
                          <div className="col-lg-3 mb-3">
                            <label className="form-label">Alert Quantity</label>
                            <input type="number" name="lowStockQuantity" className="form-control" min="1" value={formData.lowStockQuantity || ""} onKeyDown={e => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                          </div>
                        )}
                      </div>
                      <div className="card mt-2">
                        <div className="card-header"><h4 className="card-title mb-0">Description</h4></div>
                        <div className="card-body">
                          <CKEditor editor={ClassicEditor} data={formData.description} onChange={handleEditorChange}
                            config={{ licenseKey: "GPL", toolbar: ["undo", "redo", "heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "|", "outdent", "indent"] }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ══════════════════════════════════════════════════
                  02 — TECHNICAL SPECS (O-rings only)
              ══════════════════════════════════════════════════ */}
                {isOringCategory && (
                  <div className="card mb-4">
                    <a href="#tech-collapse" className={`text-body ${isTechnicalSpecsOpen ? "" : "collapsed"}`}
                      data-bs-toggle="collapse" aria-expanded={isTechnicalSpecsOpen}
                      onClick={(e) => { e.preventDefault(); setIsTechnicalSpecsOpen(!isTechnicalSpecsOpen); }}>
                      <div className="p-4">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 me-3"><div className="avatar"><div className="avatar-title custom-avatar rounded-circle bg-primary-subtle text-primary d-flex justify-content-center align-items-center"><h5 className="text-primary font-size-17 mb-0">02</h5></div></div></div>
                          <div className="flex-grow-1 overflow-hidden">
                            <h5 className="font-size-16 mb-1">Technical Specifications</h5>
                            <p className="text-muted text-truncate mb-0">AS 568A & JIS B 2401 standard sizes</p>
                          </div>
                          <i className={`mdi mdi-chevron-${isTechnicalSpecsOpen ? "up" : "down"} accor-down-icon font-size-24`}></i>
                        </div>
                      </div>
                    </a>
                    <div id="tech-collapse" className={`collapse ${isTechnicalSpecsOpen ? "show" : ""}`}>
                      <div className="p-4 border-top">
                        {/* AS 568A */}
                        <div className="mb-5">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <p className="mb-1 text-primary fw-bold" style={{ fontSize: "16px" }}><i className="mdi mdi-circle-medium me-1"></i>AS 568A Standard</p>
                              <Radio.Group
                                value={asInputMethod}
                                onChange={setAsInputMethod}
                                size="xs"
                              >
                                <Group mt="xs">
                                  <Radio value="manual" label="Manual Entry" styles={{ label: { fontSize: '12px', fontWeight: 600 } }} />
                                  <Radio value="file" label="File Upload (CSV/Excel)" styles={{ label: { fontSize: '12px', fontWeight: 600 } }} />
                                </Group>
                              </Radio.Group>
                            </div>
                            {asInputMethod === "manual" && (
                              <Group spacing="xs">
                                <Button
                                  onClick={() => { setPickerTarget("as"); setShowColumnPicker(true); }}
                                  leftIcon={<IconPlus size={14} />}
                                  variant="outline"
                                  color="blue"
                                  size="sm"
                                  styles={{ root: { borderWidth: '1.5px', fontWeight: 700 } }}
                                >
                                  Add Variant Field
                                </Button>
                                <Button onClick={handleAddAS568ASize} leftIcon={<IconPlus size={14} />} variant="light" color="blue" size="sm" styles={{ root: { fontWeight: 700 } }}>Add AS 568A Size</Button>
                              </Group>
                            )}
                          </div>

                          {asInputMethod === "file" ? (
                            <div className="mb-4">
                              <FileInput
                                label="Upload AS 568A Specifications"
                                placeholder="Choose CSV or Excel file"
                                value={asFile}
                                onChange={setAsFile}
                                clearable
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                error={errors.asFile}
                                icon={<i className="mdi mdi-upload"></i>}
                                styles={{
                                  label: { fontSize: '13px', fontWeight: 600, marginBottom: '8px' }
                                }}
                              />
                              <Text size="xs" color="dimmed" mt={5}>
                                * Uploaded file should contain columns matching the specification fields.
                              </Text>
                            </div>
                          ) : (
                            <>
                              {/* Standard-specific Variant Badges */}
                              {asSelectedExtraColumns.length > 0 && (
                                <div className="mb-3 d-flex flex-wrap gap-2 animate__animated animate__fadeIn">
                                  {asSelectedExtraColumns.map((col) => (
                                    <Badge
                                      key={col.key}
                                      variant="filled"
                                      color="blue"
                                      size="md"
                                      radius="sm"
                                      rightSection={
                                        <ActionIcon
                                          size="xs"
                                          color="blue"
                                          radius="xl"
                                          variant="transparent"
                                          onClick={() => handleRemoveExtraColumn(col.key, "as")}
                                        >
                                          <IconX size={10} color="white" stroke={4} />
                                        </ActionIcon>
                                      }
                                      styles={{
                                        root: {
                                          paddingRight: 5,
                                          height: 24,
                                          fontSize: '10px',
                                          fontWeight: 800,
                                          boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
                                        },
                                        inner: { display: 'flex', alignItems: 'center' }
                                      }}
                                    >
                                      {col.label}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {as568Rows.length > 0 ? (
                                <div className="table-responsive rounded-3 border" style={{ maxHeight: "500px", overflowY: "auto" }}>
                                  <table className="table table-bordered mb-0" style={{ fontSize: "12px", borderCollapse: "separate", borderSpacing: 0 }}>
                                    <thead style={{ background: "#1e293b", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                                      <tr>
                                        <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155", width: "50px" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">S.No</Text>
                                        </th>
                                        <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">AS 568A SIZE</Text>
                                        </th>
                                        <th colSpan="4" className="text-center" style={{ verticalAlign: "middle", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">METRIC MEASUREMENTS IN MILLIMETERS</Text>
                                        </th>
                                        {asSelectedExtraColumns.map((col) => (
                                          <th rowSpan="2" key={col.key} style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                            <Text weight={800} transform="uppercase" color="white" size="xs">{col.label}</Text>
                                          </th>
                                        ))}
                                        {/* <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">Base SKU</Text>
                                        </th> */}
                                        <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">Action</Text>
                                        </th>
                                      </tr>
                                      <tr>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">ID</Text></th>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">±</Text></th>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">CS</Text></th>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">±</Text></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {as568Rows.map((size, idx) => (
                                        <tr key={idx}>
                                          <td className="text-center text-muted" style={{ fontSize: "11px", verticalAlign: "middle" }}>{idx + 1}</td>
                                          {[["sizeCode", "A0001", "70px"], ["metric_id_mm", "0.74", "60px"], ["metric_id_tolerance_mm", "0.10", "50px"], ["metric_cs_mm", "1.02", "60px"], ["metric_cs_tolerance_mm", "0.08", "50px"]].map(([field, ph, minW]) => (
                                            <td key={field}>
                                              <TextInput
                                                placeholder={ph}
                                                type={field === "sizeCode" ? "text" : "number"}
                                                min={field === "sizeCode" ? undefined : 0}
                                                value={size[field] ?? ""}
                                                onChange={(e) => handleSizeChange("AS568A", idx, field, e.target.value)}
                                                onKeyDown={(e) => {
                                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                                    e.preventDefault();
                                                  }
                                                }}
                                                onWheel={(e) => e.target.blur()}
                                                style={{ minWidth: minW }}
                                                styles={{
                                                  input: { fontSize: "11px", color: "#334155" },
                                                  error: { fontSize: "12px", fontWeight: 600 }
                                                }}
                                                error={errors[`as_568a_standard_${field}_${idx}`]}
                                              />
                                            </td>
                                          ))}
                                          {asSelectedExtraColumns.map((col) => (
                                            <td key={col.key}>
                                              <TextInput
                                                placeholder={`Enter ${col.label}`}
                                                type="text"
                                                value={size[col.key] ?? ""}
                                                onChange={(e) => handleSizeChange("AS568A", idx, col.key, e.target.value)}
                                                style={{ minWidth: "80px" }}
                                                styles={{
                                                  input: { fontSize: "11px" },
                                                  error: { fontSize: "12px", fontWeight: 600 }
                                                }}
                                                error={errors[`as_568a_standard_${col.key}_${idx}`]}
                                              />
                                            </td>
                                          ))}
                                          {/* {[["baseSku", "Base SKU", "100px"]].map(([field, ph, minW]) => (
                                            <td key={field}>
                                              <TextInput
                                                placeholder={ph}
                                                type="text"
                                                value={size[field]}
                                                onChange={(e) => handleSizeChange("AS568A", idx, field, e.target.value)}
                                                style={{ minWidth: minW }}
                                                styles={{
                                                  input: { fontSize: "11px" },
                                                  error: { fontSize: "12px", fontWeight: 600 }
                                                }}
                                                error={errors[`as_568a_standard_${field}_${idx}`]}
                                              />
                                            </td>
                                          ))} */}
                                          <td><ActionIcon onClick={() => handleRemoveSize("AS568A", idx)} color="red" variant="light" size="xs"><IconTrash size={12} /></ActionIcon></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center py-3 border rounded">
                                  <p className="text-muted mb-2">No AS 568A sizes added yet</p>
                                  <Button onClick={handleAddAS568ASize} leftIcon={<IconPlus size={14} />} variant="outline" color="blue" size="sm">Add First AS 568A Size</Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {/* JIS B 2401 */}
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <p className="mb-1 text-success fw-bold" style={{ fontSize: "16px" }}><i className="mdi mdi-circle-medium me-1"></i>JIS B 2401 Standard</p>
                              <Radio.Group
                                value={jisInputMethod}
                                onChange={setJisInputMethod}
                                size="xs"
                              >
                                <Group mt="xs">
                                  <Radio value="manual" label="Manual Entry" styles={{ label: { fontSize: '12px', fontWeight: 600 } }} />
                                  <Radio value="file" label="File Upload (CSV/Excel)" styles={{ label: { fontSize: '12px', fontWeight: 600 } }} />
                                </Group>
                              </Radio.Group>
                            </div>
                            {jisInputMethod === "manual" && (
                              <Group spacing="xs">
                                <Button
                                  onClick={() => { setPickerTarget("jis"); setShowColumnPicker(true); }}
                                  leftIcon={<IconPlus size={14} />}
                                  variant="outline"
                                  color="green"
                                  size="sm"
                                  styles={{ root: { borderWidth: '1.5px', fontWeight: 700 } }}
                                >
                                  Add Variant Field
                                </Button>
                                <Button onClick={handleAddJISSize} leftIcon={<IconPlus size={14} />} variant="light" color="green" size="sm" styles={{ root: { fontWeight: 700 } }}>Add JIS Size</Button>
                              </Group>
                            )}
                          </div>

                          {jisInputMethod === "file" ? (
                            <div className="mb-4">
                              <FileInput
                                label="Upload JIS B 2401 Specifications"
                                placeholder="Choose CSV or Excel file"
                                value={jisFile}
                                onChange={setJisFile}
                                clearable
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                error={errors.jisFile}
                                icon={<i className="mdi mdi-upload"></i>}
                                styles={{
                                  label: { fontSize: '13px', fontWeight: 600, marginBottom: '8px' }
                                }}
                              />
                              <Text size="xs" color="dimmed" mt={5}>
                                * Uploaded file should contain columns matching the specification fields.
                              </Text>
                            </div>
                          ) : (
                            <>
                              {/* Standard-specific Variant Badges */}
                              {jisSelectedExtraColumns.length > 0 && (
                                <div className="mb-3 d-flex flex-wrap gap-2 animate__animated animate__fadeIn">
                                  {jisSelectedExtraColumns.map((col) => (
                                    <Badge
                                      key={col.key}
                                      variant="filled"
                                      color="green"
                                      size="md"
                                      radius="sm"
                                      rightSection={
                                        <ActionIcon
                                          size="xs"
                                          color="green"
                                          radius="xl"
                                          variant="transparent"
                                          onClick={() => handleRemoveExtraColumn(col.key, "jis")}
                                        >
                                          <IconX size={10} color="white" stroke={4} />
                                        </ActionIcon>
                                      }
                                      styles={{
                                        root: {
                                          paddingRight: 5,
                                          height: 24,
                                          fontSize: '10px',
                                          fontWeight: 800,
                                          boxShadow: '0 2px 4px rgba(34, 197, 94, 0.2)'
                                        },
                                        inner: { display: 'flex', alignItems: 'center' }
                                      }}
                                    >
                                      {col.label}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {jisRows.length > 0 ? (
                                <div className="table-responsive rounded-3 border" style={{ maxHeight: "500px", overflowY: "auto" }}>
                                  <table className="table table-bordered mb-0" style={{ fontSize: "12px", borderCollapse: "separate", borderSpacing: 0 }}>
                                    <thead style={{ background: "#1e293b", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                                      <tr>
                                        <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155", width: "50px" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">S.No</Text>
                                        </th>
                                        <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">JIS SIZE</Text>
                                        </th>
                                        <th colSpan="4" className="text-center" style={{ verticalAlign: "middle", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">MEASUREMENTS IN MILLIMETERS</Text>
                                        </th>
                                        {jisSelectedExtraColumns.map((col) => (
                                          <th rowSpan="2" key={col.key} style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                            <Text weight={800} transform="uppercase" color="white" size="xs">{col.label}</Text>
                                          </th>
                                        ))}
                                        {/* <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">Base SKU</Text>
                                        </th> */}
                                        <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", border: "1px solid #334155" }}>
                                          <Text weight={800} transform="uppercase" color="white" size="xs">Action</Text>
                                        </th>
                                      </tr>
                                      <tr>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">ID</Text></th>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">±</Text></th>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">CS</Text></th>
                                        <th style={{ textAlign: "center", border: "1px solid #334155" }}><Text weight={800} color="white" size="xs">±</Text></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {jisRows.map((size, idx) => (
                                        <tr key={idx}>
                                          <td className="text-center text-muted" style={{ fontSize: "11px", verticalAlign: "middle" }}>{idx + 1}</td>
                                          {[["sizeCode", "G25/P25", "70px"], ["metric_id_mm", "24.40", "60px"], ["metric_id_tolerance_mm", "0.25", "50px"], ["metric_cs_mm", "3.10", "60px"], ["metric_cs_tolerance_mm", "0.10", "50px"]].map(([field, ph, minW]) => (
                                            <td key={field}>
                                              <TextInput
                                                placeholder={ph}
                                                type={field === "sizeCode" ? "text" : "number"}
                                                min={field === "sizeCode" ? undefined : 0}
                                                value={size[field] ?? ""}
                                                onChange={(e) => handleSizeChange("JIS B 2401", idx, field, e.target.value)}
                                                onKeyDown={(e) => {
                                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                                    e.preventDefault();
                                                  }
                                                }}
                                                onWheel={(e) => e.target.blur()}
                                                style={{ minWidth: minW }}
                                                styles={{
                                                  input: { fontSize: "11px", color: "#334155" },
                                                  error: { fontSize: "12px", fontWeight: 600 }
                                                }}
                                                error={errors[`jis_b_2401_standard_${field}_${idx}`]}
                                              />
                                            </td>
                                          ))}
                                          {jisSelectedExtraColumns.map((col) => (
                                            <td key={col.key}>
                                              <TextInput
                                                placeholder={`Enter ${col.label}`}
                                                type="text"
                                                value={size[col.key] ?? ""}
                                                onChange={(e) => handleSizeChange("JIS B 2401", idx, col.key, e.target.value)}
                                                style={{ minWidth: "80px" }}
                                                styles={{
                                                  input: { fontSize: "11px" },
                                                  error: { fontSize: "12px", fontWeight: 600 }
                                                }}
                                                error={errors[`jis_b_2401_standard_${col.key}_${idx}`]}
                                              />
                                            </td>
                                          ))}
                                          {/* {[["baseSku", "Base SKU", "100px"]].map(([field, ph, minW]) => (
                                            <td key={field}>
                                              <TextInput
                                                placeholder={ph}
                                                type="text"
                                                value={size[field]}
                                                onChange={(e) => handleSizeChange("JIS B 2401", idx, field, e.target.value)}
                                                style={{ minWidth: minW }}
                                                styles={{
                                                  input: { fontSize: "11px" },
                                                  error: { fontSize: "12px", fontWeight: 600 }
                                                }}
                                                error={errors[`jis_b_2401_standard_${field}_${idx}`]}
                                              />
                                            </td>
                                          ))} */}
                                          <td><ActionIcon onClick={() => handleRemoveSize("JIS B 2401", idx)} color="red" variant="light" size="xs"><IconTrash size={12} /></ActionIcon></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center py-3 border rounded">
                                  <p className="text-muted mb-2">No JIS B 2401 sizes added yet</p>
                                  <Button onClick={handleAddJISSize} leftIcon={<IconPlus size={14} />} variant="outline" color="green" size="sm">Add First JIS Size</Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="text-muted small mt-3">
                          <p className="mb-1"><i className="mdi mdi-information-outline me-1"></i><strong>AS 568A:</strong> NOMINAL (fraction inches), STANDARD (decimal inches), METRIC (mm)</p>
                          <p className="mb-0"><i className="mdi mdi-information-outline me-1"></i><strong>JIS B 2401:</strong> G-series (static), P-series (dynamic)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ══════════════════════════════════════════════════
                  02 — PRODUCT VARIANTS with column picker
                       (shown for all non-O-ring categories)
              ══════════════════════════════════════════════════ */}
                {isVariantsCategory && (
                  <div className="card mb-4">
                    <a href="#variant-collapse" className={`text-body ${isVariantOpen ? "" : "collapsed"}`}
                      data-bs-toggle="collapse" aria-expanded={isVariantOpen}
                      onClick={(e) => { e.preventDefault(); setIsVariantOpen(!isVariantOpen); }}>
                      <div className="p-4">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 me-3"><div className="avatar"><div className="avatar-title custom-avatar rounded-circle bg-primary-subtle text-primary d-flex justify-content-center align-items-center"><h5 className="text-primary font-size-17 mb-0">02</h5></div></div></div>
                          <div className="flex-grow-1 overflow-hidden">
                            <h5 className="font-size-16 mb-1">Product Variants</h5>
                            <p className="text-muted text-truncate mb-0">
                              {variantRows.length > 0
                                ? `${variantRows.length} variant(s) | ${fullColumnList.map(c => c.label).join(", ")}`
                                : "Choose columns and add size / material / dimension variants"}
                            </p>
                          </div>
                          <i className={`mdi mdi-chevron-${isVariantOpen ? "up" : "down"} accor-down-icon font-size-24`}></i>
                        </div>
                      </div>
                    </a>

                    <div id="variant-collapse" className={`collapse ${isVariantOpen ? "show" : ""}`}>
                      <div className="p-4 border-top">

                        {/* ── Compact Column Selector ─────────────────────────── */}
                        <div
                          className="mb-3 px-4 py-3 rounded-3"
                          style={{
                            background: "#f8fafc",
                            border: "1.5px solid #e2e8f0",
                            boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                              <div
                                style={{
                                  width: "4px", height: "16px", borderRadius: "4px",
                                  background: "#1e293b",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 800,
                                  color: "#475569",
                                  letterSpacing: "0.1em",
                                  textTransform: "uppercase",
                                }}
                              >
                                Variant Fields
                              </span>
                            </div>

                            <Group spacing={8}>
                              <Button
                                variant="outline"
                                size="xs"
                                leftSection={<i className="mdi mdi-download font-size-14" />}
                                onClick={handleDownloadTemplate}
                                color="indigo"
                                styles={{ root: { height: '28px', padding: '0 12px', fontSize: '11px', fontWeight: 700 } }}
                              >
                                Download Template
                              </Button>
                              <Button
                                variant="outline"
                                size="xs"
                                leftSection={<i className="mdi mdi-upload font-size-14" />}
                                onClick={() => uploadInputRef.current?.click()}
                                color="teal"
                                styles={{ root: { height: '28px', padding: '0 12px', fontSize: '11px', fontWeight: 700 } }}
                              >
                                Upload Excel
                              </Button>
                              <input
                                type="file"
                                ref={uploadInputRef}
                                hidden
                                accept=".xlsx,.xls,.csv"
                                onChange={handleUploadExcel}
                              />
                            </Group>
                          </div>

                          <Group spacing={6} align="center" style={{ flexWrap: "wrap" }}>
                            {/* Common columns — permanently shown */}
                            {COMMON_COLUMNS.map((col) => (
                              <Tooltip label="Required field — cannot be removed" key={col.key} withArrow>
                                <div
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    padding: "5px 11px",
                                    borderRadius: "8px",
                                    background: "#dbeafe",
                                    border: "1px solid #93c5fd",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#1d4ed8",
                                    cursor: "default",
                                    userSelect: "none",
                                  }}
                                >
                                  <IconCheck size={10} stroke={3} color="#1d4ed8" />
                                  {col.label}
                                </div>
                              </Tooltip>
                            ))}

                            {/* Selected extra columns — removable */}
                            {selectedExtraColumns.map((col) => (
                              <div
                                key={col.key}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "5px 8px 5px 11px",
                                  borderRadius: "8px",
                                  background: "#ffffff",
                                  border: "1.5px solid #cbd5e1",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: "#334155",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                }}
                              >
                                {col.label}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveExtraColumn(col.key)}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    background: "#fee2e2",
                                    border: "1px solid #fecaca",
                                    color: "#dc2626",
                                    cursor: "pointer",
                                    padding: 0,
                                    flexShrink: 0,
                                  }}
                                  title={`Remove ${col.label}`}
                                >
                                  <IconX size={9} stroke={3} />
                                </button>
                              </div>
                            ))}

                            {/* Add Button */}
                            <button
                              type="button"
                              onClick={() => setShowColumnPicker(true)}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "5px 12px",
                                borderRadius: "8px",
                                background: "#1e293b",
                                border: "none",
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#ffffff",
                                cursor: "pointer",
                                letterSpacing: "0.03em",
                                transition: "background 0.15s ease",
                                boxShadow: "0 2px 6px rgba(15,23,42,0.2)",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#0f172a"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "#1e293b"; }}
                            >
                              <IconPlus size={11} stroke={3} />
                              Add Field
                            </button>
                          </Group>
                        </div>


                        {/* ── Variant Table ────────────────────────────── */}
                        {variantRows.length > 0 ? (
                          <div className="table-responsive rounded-3 border bg-white" style={{ maxHeight: "500px", overflowY: "auto", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                            <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px", borderCollapse: "separate", borderSpacing: 0 }}>
                              <thead style={{ background: "#1e293b", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                  <th style={{ width: "50px", textAlign: "center", borderBottom: "1px solid #334155" }}>
                                    <Text weight={800} transform="uppercase" color="white" size="xs">S.No</Text>
                                  </th>
                                  {selectedExtraColumns.map((col) => (
                                    <th key={col.key} style={{ borderBottom: "1px solid #334155" }}>
                                      <Text weight={800} transform="uppercase" color="white" size="xs">
                                        {col.label}
                                      </Text>
                                    </th>
                                  ))}
                                  {COMMON_COLUMNS.map((col) => (
                                    <th key={col.key} style={{ borderBottom: "1px solid #334155" }}>
                                      <Text weight={800} transform="uppercase" color="white" size="xs">
                                        {col.label}
                                      </Text>
                                    </th>
                                  ))}
                                  {currencies.map((curr) => (
                                    <th key={curr._id} style={{ borderBottom: "1px solid #334155" }}>
                                      <Text weight={800} transform="uppercase" color="white" size="xs">
                                        {curr.code} Price
                                      </Text>
                                    </th>
                                  ))}
                                  <th style={{ width: "80px", textAlign: "center", borderBottom: "1px solid #334155" }}>
                                    <Text weight={800} transform="uppercase" color="white" size="xs">Action</Text>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {variantRows.map((row, rowIdx) => (
                                  <tr key={rowIdx}>
                                    <td className="text-center text-muted" style={{ fontSize: "11px" }}>{rowIdx + 1}</td>
                                    {selectedExtraColumns.map((col) => (
                                      <td key={col.key}>
                                        <TextInput
                                          size="xs"
                                          variant="filled"
                                          placeholder={`Enter ${col.label}...`}
                                          error={errors[`v_${col.key}_${rowIdx}`]}
                                          value={row[col.key] ?? ""}
                                          onChange={(e) => handleVariantRowChange(rowIdx, col.key, e.target.value)}
                                          styles={{
                                            input: { fontSize: '12px', color: "#334155" },
                                            error: { fontSize: "12px", fontWeight: 600 }
                                          }}
                                        />
                                      </td>
                                    ))}
                                    {COMMON_COLUMNS.map((col) => (
                                      <td key={col.key}>
                                        <TextInput
                                          size="xs"
                                          variant="filled"
                                          type={col.type}
                                          min={col.type === "number" ? 0 : undefined}
                                          placeholder={col.placeholder}
                                          error={errors[`v_${col.key}_${rowIdx}`]}
                                          value={row[col.key] ?? ""}
                                          onChange={(e) => handleVariantRowChange(rowIdx, col.key, e.target.value)}
                                          onKeyDown={(e) => {
                                            const blocked = col.key === "stock" ? ['e', 'E', '+', '-', '.'] : ['e', 'E', '+', '-'];
                                            if (blocked.includes(e.key)) {
                                              e.preventDefault();
                                            }
                                          }}
                                          onWheel={(e) => e.target.blur()}
                                          readOnly={col.readOnly}
                                          styles={{
                                            input: { fontSize: '12px', color: "#334155", backgroundColor: col.readOnly ? "#f8fafc" : undefined },
                                            error: { fontSize: "12px", fontWeight: 600 }
                                          }}
                                        />
                                      </td>
                                    ))}
                                    {currencies.map((curr) => (
                                      <td key={curr._id}>
                                        <TextInput
                                          size="xs"
                                          variant="filled"
                                          type="number"
                                          min={0}
                                          placeholder={`${curr.symbol} Price`}
                                          error={errors[`v_price_${curr._id}_${rowIdx}`]}
                                          value={(row.pricing || []).find(p => p.currencyId === curr._id)?.price ?? ""}
                                          onChange={(e) => handleVariantRowChange(rowIdx, `price_${curr._id}`, e.target.value)}
                                          onKeyDown={(e) => {
                                            if (['e', 'E', '+', '-'].includes(e.key)) {
                                              e.preventDefault();
                                            }
                                          }}
                                          onWheel={(e) => e.target.blur()}
                                          styles={{
                                            input: { fontSize: '12px' },
                                            error: { fontSize: "12px", fontWeight: 600 }
                                          }}
                                        />
                                      </td>
                                    ))}
                                    <td className="text-center">
                                      <Tooltip label="Remove variation">
                                        <ActionIcon
                                          onClick={() => handleRemoveVariantRow(rowIdx)}
                                          color="red"
                                          variant="light"
                                          size="sm"
                                        >
                                          <IconTrash size={14} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="py-5 border rounded-3 bg-light d-flex flex-column align-items-center justify-content-center" style={{ borderStyle: "dashed !important", background: "rgba(248, 249, 250, 0.5)" }}>
                            <Stack align="center" spacing="xs">
                              <IconColumns size={32} color="#adb5bd" stroke={1.5} />
                              <Stack spacing={2} align="center">
                                <Text weight={600} size="sm" color="gray.7">No variants configured</Text>
                                <Text size="xs" color="dimmed" align="center">
                                  Add columns above to define attributes, then add variation rows.
                                </Text>
                              </Stack>
                              <Button
                                onClick={handleAddVariantRow}
                                leftIcon={<IconPlus size={14} />}
                                variant="light"
                                color="indigo"
                                size="sm"
                                mt="sm"
                              >
                                Add First variation
                              </Button>
                            </Stack>
                          </div>
                        )}

                        {/* Add row button */}
                        {variantRows.length > 0 && (
                          <Group position="right" mt="sm">
                            <Button
                              onClick={handleAddVariantRow}
                              leftIcon={<IconPlus size={16} />}
                              variant="subtle"
                              color="indigo"
                              size="sm"
                              styles={{ root: { fontWeight: 500 } }}
                            >
                              Add another variation
                            </Button>
                          </Group>
                        )}

                      </div>
                    </div>
                  </div>
                )}

                {/* ══════════════════════════════════════════════════
                  03 — META TAGS
              ══════════════════════════════════════════════════ */}
                <div className="card mb-4">
                  <div className="text-body collapsed" style={{ cursor: "pointer" }}
                    data-bs-toggle="collapse" data-bs-target="#meta-collapse"
                    onClick={() => setIsMetaTagsOpen(!isMetaTagsOpen)}>
                    <div className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-3"><div className="avatar"><div className="avatar-title custom-avatar rounded-circle bg-primary-subtle text-primary d-flex justify-content-center align-items-center"><h5 className="text-primary font-size-17 mb-0">03</h5></div></div></div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h5 className="font-size-16 mb-1">Product Meta Tags</h5>
                          <p className="text-muted text-truncate mb-0">SEO information</p>
                        </div>
                        <i className={`mdi mdi-chevron-${isMetaTagsOpen ? "up" : "down"} accor-down-icon font-size-24`}></i>
                      </div>
                    </div>
                  </div>
                  <div id="meta-collapse" className={`collapse ${isMetaTagsOpen ? "show" : ""}`}>
                    <div className="p-4 border-top">
                      <div className="col-12 mb-3"><label className="form-label">Meta Title</label><input type="text" name="metaTitle" className="form-control" value={formData.metaTitle} onChange={handleChange} /></div>
                      <div className="col-12 mb-3"><label className="form-label">Meta Keywords</label><input type="text" name="metaKey" className="form-control" value={formData.metaKey} onChange={handleChange} /></div>
                      <div className="col-12 mb-3"><label className="form-label">Meta Description</label><textarea name="metaDescription" className="form-control" rows="3" value={formData.metaDescription} onChange={handleChange} /></div>
                    </div>
                  </div>
                </div>

                {/* ══════════════════════════════════════════════════
                  04 — IMAGES
              ══════════════════════════════════════════════════ */}
                <div className="card mb-4">
                  <a onClick={() => setImageOpen(!imageOpen)} className={`text-body ${imageOpen ? "" : "collapsed"}`} style={{ cursor: "pointer" }}>
                    <div className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-3"><div className="avatar"><div className="avatar-title custom-avatar rounded-circle bg-primary-subtle text-primary d-flex justify-content-center align-items-center"><h5 className="text-primary font-size-17 mb-0">04</h5></div></div></div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h5 className="font-size-16 mb-1">Images</h5>
                          <p className="text-muted text-truncate mb-0">Product & additional images</p>
                        </div>
                        <i className={`mdi mdi-chevron-${imageOpen ? "up" : "down"} accor-down-icon font-size-24`}></i>
                      </div>
                    </div>
                  </a>
                  {imageOpen && (
                    <div className="p-4 border-top">
                      <div className="row">
                        <div className="col-lg-3">
                          <label className="form-label">Product Image <span className="text-danger">*</span></label>
                          <input className="form-control" type="file" accept="image/*" onChange={(e) => handleFileChange(e, "productImage")} />
                          {errors.productImage && <div className="text-danger small">{errors.productImage}</div>}
                          <div className="mt-2 d-flex flex-wrap gap-2">
                            {(formData.productImage || []).map((img, i) => (
                              <div key={i} style={{ position: "relative" }}>
                                <img src={img instanceof File ? URL.createObjectURL(img) : `${IMAGE_BASE_URL}${img.docPath}/${img.docName}`} className="img-thumbnail" style={{ width: "60px", height: "60px", objectFit: "cover" }} />
                                <button type="button" onClick={() => handleDeleteImage(i)} className="image-remove-btn">×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="col-lg-3">
                          <label className="form-label">Additional Images</label>
                          <input className="form-control" type="file" multiple accept="image/*" onChange={(e) => handleFileChange(e, "additionalImages")} />
                          <div className="mt-2 d-flex flex-wrap gap-2">
                            {(formData.additionalImages || []).map((img, i) => (
                              <div key={i} style={{ position: "relative" }}>
                                <img src={img instanceof File ? URL.createObjectURL(img) : `${IMAGE_BASE_URL}${img.docPath}/${img.docName}`} className="img-thumbnail" style={{ width: "60px", height: "60px", objectFit: "cover" }} />
                                <button type="button" onClick={() => handleDeleteAdditionalImage(i)} className="image-remove-btn">×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* ── Column Picker Modal (Global) ─────────────────────────── */}
          <Modal
            opened={showColumnPicker}
            onClose={() => { setShowColumnPicker(false); setVariantSearch(""); setPickerTarget("common"); setIsFilterCheckBox(false); setIsEditingExistVariant(false); setEditingVariantId(null); }}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "30px", height: "30px", borderRadius: "8px",
                    background: "#1e293b",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconColumns size={15} color="#fff" />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "15px", color: "#ffffff", lineHeight: 1.2 }}>
                    {isEditingExistVariant ? "Edit Variant" : `Add ${pickerTarget === "as" ? "AS 568A" : pickerTarget === "jis" ? "JIS B 2401" : "Variant"} Field`}
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>
                    Search existing or create new
                  </div>
                </div>
              </div>
            }
            centered
            size="md"
            overlayColor="#0f172a"
            overlayOpacity={0.65}
            styles={{
              modal: {
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
                border: "1px solid #0f172a",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased"
              },
              header: {
                padding: "18px 24px",
                background: "#1e293b",
                color: "#ffffff",
                borderBottom: "none"
              },
              body: { padding: "24px", background: "#ffffff" },
              close: {
                color: "#ffffff",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff"
                }
              }
            }}
          >
            <Stack spacing="sm">
              <TextInput
                placeholder="Search or type new variant name..."
                icon={<IconSearch size={14} color="#94a3b8" />}
                value={variantSearch}
                onChange={(e) => setVariantSearch(e.target.value)}
                autoFocus
                styles={{
                  input: {
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    fontSize: "14px",
                    background: "#f8fafc",
                    padding: "12px 14px 12px 40px",
                    height: "45px",
                    color: "#0f172a",
                    fontWeight: 500,
                    "&:focus": { borderColor: "#1e293b", background: "#fff", boxShadow: "0 0 0 4px rgba(30, 41, 59, 0.05)" },
                  },
                }}
                rightSection={
                  variantSearch && (
                    <ActionIcon
                      size="xs"
                      radius="xl"
                      variant="filled"
                      onClick={() => { setVariantSearch(""); if (isEditingExistVariant) cancelEditVariant(); }}
                      styles={{ root: { background: "#cbd5e1", color: "#475569", "&:hover": { background: "#cbd5e1" } } }}
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  )
                }
              />

              {(isEditingExistVariant || (variantSearch.trim() !== "" && !unselectedColumns.some(col => col.label.toLowerCase() === variantSearch.toLowerCase()))) && (
                <div className="d-flex align-items-center gap-2 mb-2 animate__animated animate__fadeIn">
                  <input
                    type="checkbox"
                    id="isFilterCheckBox"
                    className="form-check-input mt-0"
                    checked={!!isFilterCheckBox}
                    onChange={(e) => setIsFilterCheckBox(e.target.checked)}
                    style={{ cursor: "pointer", width: "18px", height: "18px" }}
                  />
                  <label
                    htmlFor="isFilterCheckBox"
                    className="form-label mb-0"
                    style={{ fontSize: "14px", fontWeight: 700, cursor: "pointer", color: "#000000" }}
                  >
                    Is Filter
                  </label>
                </div>
              )}

              <div>
                <Text
                  size="11px"
                  weight={800}
                  mb={12}
                  style={{
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "#000000",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: 900
                  }}
                >
                  Available Fields
                </Text>
                <ScrollArea h={190} offsetScrollbars>
                  <Stack spacing={2}>
                    {unselectedColumns
                      .filter((col) =>
                        col.label.toLowerCase().includes(variantSearch.toLowerCase())
                      )
                      .map((col) => (
                        <button
                          key={col.key}
                          type="button"
                          onClick={() => {
                            handleAddExtraColumn(col);
                            setShowColumnPicker(false);
                            setVariantSearch("");
                            setIsFilterCheckBox(false);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            width: "100%",
                            padding: "9px 12px",
                            borderRadius: "8px",
                            background: "transparent",
                            border: "1px solid transparent",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1e293b",
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "all 0.2s ease",
                            justifyContent: "space-between",
                            borderBottom: "1px solid #f8fafc"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f8fafc";
                            e.currentTarget.style.color = "#000";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color = "#1e293b";
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexGrow: 1 }}>
                            <span style={{ color: "#000000", fontWeight: 700 }}>{col.label}</span>
                            {col.isFilter && (
                              <Badge
                                size="xs"
                                variant="filled"
                                styles={{
                                  root: {
                                    background: "#2563eb",
                                    padding: "0 10px",
                                    height: "20px",
                                    fontSize: "10px",
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    color: "#fff"
                                  }
                                }}
                              >
                                Filter
                              </Badge>
                            )}
                          </div>
                          <ActionIcon
                            size="md"
                            variant="light"
                            color="blue"
                            onClick={(e) => { e.stopPropagation(); startEditVariant(col); }}
                            styles={{ root: { backgroundColor: "#eff6ff", "&:hover": { backgroundColor: "#dbeafe" } } }}
                          >
                            <i className="mdi mdi-pencil" style={{ fontSize: "16px", color: "#2563eb" }}></i>
                          </ActionIcon>
                        </button>
                      ))}

                    {variantSearch.trim() !== "" && !isEditingExistVariant &&
                      !unselectedColumns.some(
                        (col) => col.label.toLowerCase() === variantSearch.toLowerCase()
                      ) && (
                        <button
                          type="button"
                          onClick={handleCreateNewVariant}
                          disabled={isCreatingVariant}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "100%",
                            padding: "12px 14px",
                            marginTop: "12px",
                            borderRadius: "12px",
                            background: "#1e293b",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#fff",
                            cursor: isCreatingVariant ? "not-allowed" : "pointer",
                            opacity: isCreatingVariant ? 0.7 : 1,
                            transition: "all 0.2s ease",
                            border: "none",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                          }}
                          onMouseEnter={(e) => {
                            if (!isCreatingVariant) {
                              e.currentTarget.style.background = "#0f172a";
                              e.currentTarget.style.transform = "translateY(-1px)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#1e293b";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          {isCreatingVariant ? (
                            <Loader size={14} color="white" />
                          ) : (
                            <IconPlus size={14} stroke={3} />
                          )}
                          {isCreatingVariant ? "Creating..." : `Create & Add "${variantSearch}"`}
                        </button>
                      )}

                    {isEditingExistVariant && (
                      <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                        <button
                          type="button"
                          onClick={handleUpdateVariant}
                          disabled={isCreatingVariant}
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            padding: "12px 14px",
                            borderRadius: "12px",
                            background: "#1e293b",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#fff",
                            cursor: isCreatingVariant ? "not-allowed" : "pointer",
                            opacity: isCreatingVariant ? 0.7 : 1,
                            transition: "all 0.2s ease",
                            border: "none",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                          }}
                        >
                          {isCreatingVariant ? <Loader size={14} color="white" /> : "Update Variant"}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditVariant}
                          style={{
                            padding: "12px 20px",
                            borderRadius: "12px",
                            background: "#f8fafc",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#475569",
                            border: "1.5px solid #e2e8f0",
                            transition: "all 0.2s ease",
                            cursor: "pointer"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f1f5f9";
                            e.currentTarget.style.borderColor = "#cbd5e1";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#f8fafc";
                            e.currentTarget.style.borderColor = "#e2e8f0";
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {variantSearch.trim() === "" && unselectedColumns.length === 0 && (
                      <div style={{ textAlign: "center", padding: "24px 0" }}>
                        <IconCheck size={24} color="#86efac" style={{ marginBottom: "6px" }} />
                        <Text size="xs" color="dimmed">All fields have been added.</Text>
                      </div>
                    )}
                  </Stack>
                </ScrollArea>
              </div>
            </Stack>
          </Modal>

          <div className="modal-footer mt-3">
            <button type="button" onClick={handleClose} className="btn btn-secondary me-1 px-32" style={{ height: "45px" }}>Cancel</button>
            <button type="submit" className="btn btn-primary px-32" style={{ height: "45px" }} onClick={handleSubmit}>{isEditing ? "Update" : "Save"}</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateProductListLayer;